import { validateUserNameValue } from "../../constants/validate";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator";
import {
  apiUpdateMemberEmail,
  apiUpdateMemberMobile,
  apiUpdateMemberUserName,
} from "../../api/backend/auth";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";

export const handlePhoneNumberSubmit = async ({
  phoneNumberValue,
  setPhoneNumberValueError,
  setPhoneNumberSuggestionError,
  setPhoneNumberValue,
  setLoading,
  currentMemberId,
  formatPhoneNumber,
  setCurrentMemberData,
  currentContextType,
  handleLogout,
  notificationsCountFetchedFromMiddleware, // <- hydrate badge if backend sends a count
}) => {
  const loggedInAccountHolderId = currentMemberId;
  if (!phoneNumberValue) {
    setPhoneNumberValueError("Phone number is required");
    return { ok: false, unreadNotificationCount: null };
  }
  setLoading(true);
  const formattedPhoneNumber = formatPhoneNumber(phoneNumberValue);
  const phoneNumberStorageKey = getStorageKey(
    "phoneNumberValue",
    currentContextType
  );
  try {
    const response = await apiUpdateMemberMobile(
      loggedInAccountHolderId,
      formattedPhoneNumber
    );
    // eject if suspended (success-path response)
    if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
      await handleLogout();
      return { ok: false, reason: "eject", unreadNotificationCount: null };
    }
    // hydrate badge if server sent a count
    const count = response?.data?.unreadNotificationCount ?? null;
    if (typeof count === "number") {
      notificationsCountFetchedFromMiddleware(count);
    }
    const msg = response?.data?.message;
    if (msg === "Phone number updated successfully") {
      setPhoneNumberSuggestionError("");
      setCurrentMemberData((prev) => ({
        ...prev,
        phoneNumber: formattedPhoneNumber,
      }));
      setPhoneNumberValueError("");
      setPhoneNumberValue("");
      localStorage.removeItem(phoneNumberStorageKey);
      return { ok: true, unreadNotificationCount: count };
    }

    setPhoneNumberSuggestionError(
      msg || "Unexpected error updating phone number"
    );
    return { ok: false, unreadNotificationCount: count };
  } catch (error) {
    // eject if suspended (error-path response)
    if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
      await handleLogout();
      return { ok: false, reason: "eject", unreadNotificationCount: null };
    }
    // hydrate badge from error payloads if present
    const count =
      error?.response?.data?.unreadNotificationCount ??
      error?.unreadNotificationCount ??
      null;
    if (typeof count === "number") {
      notificationsCountFetchedFromMiddleware(count);
    }

    setPhoneNumberSuggestionError(
      error?.response?.data?.message || "Error updating phone number"
    );
    return { ok: false, unreadNotificationCount: count };
  } finally {
    setLoading(false);
  }
};

export const handleEmailSubmit = async ({
  emailValue,
  setEmailValueError,
  setEmailExistError,
  setEmailValue,
  setLoading,
  currentMemberId,
  setCurrentMemberData,
  handleLogout,
  notificationsCountFetchedFromMiddleware,
}) => {
  const loggedInAccountHolderId = currentMemberId;
  if (!emailValue) return { ok: false, unreadNotificationCount: null };

  setLoading(true);
  try {
    const response = await apiUpdateMemberEmail(
      loggedInAccountHolderId,
      emailValue
    );
    // eject guard (success path)
    if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
      await handleLogout();
      return { ok: false, reason: "eject", unreadNotificationCount: null };
    }

    // hydrate badge (success path)
    const count = response?.data?.unreadNotificationCount ?? null;
    if (typeof count === "number")
      notificationsCountFetchedFromMiddleware(count);

    if (response?.data?.message === "Email updated successfully") {
      setEmailValueError("");
      setEmailExistError("");
      setCurrentMemberData?.((p) => ({ ...p, email: emailValue }));
      setEmailValue("");
      return { ok: true, unreadNotificationCount: count };
    }

    const msg = response?.data?.message || "Error updating email";
    if (/exist|in use|taken/i.test(msg)) setEmailExistError(msg);
    else setEmailValueError(msg);
    return { ok: false, unreadNotificationCount: count };
  } catch (error) {
    // eject guard (error path)
    if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
      await handleLogout();
      return { ok: false, reason: "eject", unreadNotificationCount: null };
    }

    const status = error?.response?.status;
    const { message } = error?.response?.data || {};
    if (status === 400 && message) {
      if (/exist|in use|taken/i.test(message)) setEmailExistError(message);
      else setEmailValueError(message);
    } else {
      setEmailValueError("Error updating email");
    }

    // hydrate badge (error path)
    const count =
      error?.response?.data?.unreadNotificationCount ??
      error?.unreadNotificationCount ??
      null;
    if (typeof count === "number")
      notificationsCountFetchedFromMiddleware(count);

    return { ok: false, unreadNotificationCount: count };
  } finally {
    setLoading(false);
  }
};

export const handleUsernameSubmit = async ({
  userNameValue,
  setUsernameError,
  setUserNameValue,
  setSuggestedUsernames,
  setLoading,
  currentMemberId,
  setCurrentMemberData,
  currentContextType, // kept for your storage key
  handleLogout,
  notificationsCountFetchedFromMiddleware,
}) => {
  const loggedInAccountHolderId = currentMemberId;
  const value = (userNameValue || "").trim();
  if (!value) {
    setUsernameError("Username is required");
    return { ok: false, unreadNotificationCount: null };
  }

  setLoading(true);
  const usernameStorageKey = getStorageKey("userNameValue", currentContextType);

  try {
    const response = await apiUpdateMemberUserName(loggedInAccountHolderId, value);

    // eject guard (success path)
    if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
      await handleLogout();
      return { ok: false, reason: "eject", unreadNotificationCount: null };
    }

    // hydrate badge (success path)
    const count = response?.data?.unreadNotificationCount ?? null;
    if (typeof count === "number")
      notificationsCountFetchedFromMiddleware(count);

    const msg = response?.data?.message;
    if (msg === "Username updated successfully" || response?.status === 200) {
      setUsernameError("");
      setSuggestedUsernames([]);
      setCurrentMemberData((p) => ({ ...p, username: value }));
      setUserNameValue("");
      localStorage.removeItem(usernameStorageKey);
      return { ok: true, username: value, unreadNotificationCount: count };
    }

    setUsernameError(msg || "Error updating username");
    return { ok: false, unreadNotificationCount: count };
  } catch (error) {
    // eject guard (error path)
    if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
      await handleLogout();
      return { ok: false, reason: "eject", unreadNotificationCount: null };
    }

    const status = error?.response?.status;
    const { message, suggestedUsernames } = error?.response?.data || {};
    if (status === 400 || status === 409) {
      if (message) setUsernameError(message);
      if (Array.isArray(suggestedUsernames)) {
        setSuggestedUsernames(suggestedUsernames);
      }
    } else {
      setUsernameError("Error updating username");
    }

    // hydrate badge (error path)
    const count =
      error?.response?.data?.unreadNotificationCount ??
      error?.unreadNotificationCount ??
      null;
    if (typeof count === "number")
      notificationsCountFetchedFromMiddleware(count);

    return { ok: false, unreadNotificationCount: count };
  } finally {
    setLoading(false);
  }
};

// Clear function for clearing the input and triggering validation
export const handleClearUsername = ({
  setUserNameValue,
  setUserNameValidationRules,
  setUserNameToolTipVisible,
}) => {
  setUserNameValue(""); // Clear the input
  const validationResults = validateUserNameValue(""); // Validate empty input
  setUserNameValidationRules(validationResults); // Set validation for empty field
  // Show tooltip since it's cleared and invalid
  setUserNameToolTipVisible(true);
};
